'use client';

import Image from 'next/image';
import Link from 'next/link';
import { FormEvent, useState } from 'react';
import { authClient } from '@/lib/auth-client';
import { branding } from '@/config/branding';
import ReferenceIcon from '@/components/ReferenceIcon';

function AuthHeading({ signingUp }: { signingUp: boolean }) {
  return <header className="auth-reference-heading">
    <Link className="auth-reference-brand" href="/" aria-label={`${branding.productName} home`}><Image unoptimized src={branding.auth.logoPath} alt={branding.productName} width={28} height={28} priority /></Link>
    <h1>{signingUp ? 'Create your account' : `Log in to ${branding.productName}`}</h1>
    <p>{signingUp ? `Welcome to ${branding.productName}` : `Welcome back to ${branding.productName}`}</p>
  </header>;
}

function AuthFooter() {
  return <footer className="auth-reference-footer">
    <div className="auth-reference-security" title="Wallet keys are encrypted by Cabal."><span>{branding.auth.providerStatus}</span></div>
    <div className="auth-reference-rule" />
    <nav className="auth-reference-links" aria-label="Cabal resources"><a href={branding.docsUrl} aria-label="Documentation" target="_blank" rel="noreferrer"><ReferenceIcon name="docs" size={14} /></a><a href={branding.social.telegram} aria-label="Telegram" target="_blank" rel="noreferrer"><ReferenceIcon name="telegram" size={16} /></a><a href={branding.social.x} aria-label="X" target="_blank" rel="noreferrer"><ReferenceIcon name="x" size={16} /></a></nav>
  </footer>;
}

export default function AuthForm({ mode }: { mode: 'sign-in' | 'sign-up' }) {
  const signingUp = mode === 'sign-up';
  const [step, setStep] = useState<'methods' | 'email'>(signingUp ? 'email' : 'methods');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    setError('');
    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') || '');
    const password = String(form.get('password') || '');
    try {
      const result = signingUp
        ? await authClient.signUp.email({ name: String(form.get('name') || ''), email, password })
        : await authClient.signIn.email({ email, password, rememberMe: true });
      if (result.error) { setError(result.error.message || 'Unable to authenticate.'); return; }
      if (signingUp) {
        const referralCode = new URLSearchParams(window.location.search).get('ref');
        if (referralCode) {
          try {
            const attribution = await fetch('/api/referrals/attribute', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: referralCode }),
            });
            if (!attribution.ok) sessionStorage.setItem('proxima-referral-notice', 'The account was created, but this referral link could not be applied.');
          } catch {
            sessionStorage.setItem('proxima-referral-notice', 'The account was created, but this referral link could not be applied.');
          }
        }
      }
      window.location.assign('/workspace');
    } catch { setError('Could not connect. Please try again.'); }
    finally { setPending(false); }
  };
  return <main className="auth-reference-page"><section className="auth-reference-main" aria-label={signingUp ? 'Create account' : 'Sign in'}>
    <AuthHeading signingUp={signingUp} />
    {step === 'methods' ? <div className="auth-reference-methods">
      <button className="auth-reference-method" type="button" onClick={() => { setError(''); setStep('email'); }}><ReferenceIcon name="email" size={14} /><span>Continue with Email</span><span className="auth-reference-last-used">Last used</span></button>
      <button className="auth-reference-method" type="button" aria-disabled="true" title="Passkey sign-in is not configured" onClick={() => setError('Passkey sign-in is not configured yet. Continue with Email to sign in.')}><ReferenceIcon name="passkey" size={15} /><span>Continue with Passkey</span></button>
      {error && <div className="auth-reference-error" role="alert">{error}</div>}
    </div> : <form className="auth-reference-form" onSubmit={submit}>
      {signingUp && <label>Name<input name="name" autoComplete="name" required maxLength={80} /></label>}
      <label>Email<input name="email" type="email" autoComplete="email" placeholder="Enter your email" required autoFocus /></label>
      <label>Password<input name="password" type="password" autoComplete={signingUp ? 'new-password' : 'current-password'} minLength={8} required /></label>
      {error && <div className="auth-reference-error" role="alert">{error}</div>}
      <button className="auth-reference-migrate" disabled={pending} type="submit">{pending ? 'Please wait…' : signingUp ? 'Create Account' : 'Log in'}</button>
      {!signingUp && <button className="auth-reference-back" type="button" disabled={pending} onClick={() => { setStep('methods'); setError(''); }}>Back</button>}
    </form>}
    <p className="auth-reference-switch">{signingUp ? 'Already have an account?' : "Don't have an account?"} <Link href={signingUp ? '/login' : '/signup'}>{signingUp ? 'Log in' : 'Create Account'}</Link></p>
    <AuthFooter />
  </section></main>;
}
